import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { FiMail, FiLock, FiLogIn, FiKey, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import { Button, Input } from '../components/ui';
import AuthSidePanel from '../components/AuthSidePanel';

const LoginPage = () => {
    const eventTitle = process.env.REACT_APP_EVENT_TITLE || 'EventSawa';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || '/dashboard';

    const [loginError, setLoginError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoginError('');
        setIsLoading(true);
        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (error) {
            setLoginError(
                error.response?.data?.error || error.response?.data?.detail || error.message ||
                'Login failed. Please check your credentials and try again.'
            );
            setIsLoading(false);
            return false;
        }
        setIsLoading(false);
        return false;
    };

    return (
        <section className="min-h-screen grid lg:grid-cols-2 bg-white dark:bg-gray-900">
            <Helmet>
                <title>{`Sign in | ${eventTitle}`}</title>
                <meta name="description" content="Sign in to your EventSawa account" />
            </Helmet>

            {/* Form (left on desktop, top on mobile) */}
            <div className="order-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">Welcome back</h1>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">Sign in to continue to {eventTitle}.</p>
                    </div>

                    <form className="space-y-5" onSubmit={handleSubmit}>
                        <Input
                            type="email" name="email" label="Email address" placeholder="name@company.com"
                            leftIcon={<FiMail className="w-5 h-5" />}
                            value={email} onChange={(e) => setEmail(e.target.value)} required
                        />
                        <Input
                            type={showPassword ? 'text' : 'password'} name="password" label="Password" placeholder="••••••••"
                            leftIcon={<FiLock className="w-5 h-5" />}
                            rightIcon={
                                <button type="button" onClick={() => setShowPassword((s) => !s)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                    {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                                </button>
                            }
                            value={password} onChange={(e) => setPassword(e.target.value)} required
                        />

                        <div className="flex items-center justify-between">
                            <label className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                                <input type="checkbox" className="w-4 h-4 mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600" />
                                Remember me
                            </label>
                            <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 inline-flex items-center">
                                <FiKey className="w-4 h-4 mr-1" /> Forgot password?
                            </Link>
                        </div>

                        {loginError && (
                            <div className="flex items-center p-3 text-sm text-red-700 bg-red-50 rounded-lg dark:bg-red-900/20 dark:text-red-400" role="alert">
                                <FiAlertCircle className="mr-2 flex-shrink-0" /> {loginError}
                            </div>
                        )}

                        <Button type="submit" size="lg" fullWidth loading={isLoading} leftIcon={<FiLogIn className="w-5 h-5" />}>
                            Sign in
                        </Button>

                        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                            Don't have an account?{' '}
                            <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                                Create account
                            </Link>
                        </p>
                    </form>
                </div>
            </div>

            {/* Catchy panel (right on desktop, below on mobile) */}
            <div className="order-2">
                <AuthSidePanel />
            </div>
        </section>
    );
};

export default LoginPage;
